/**
 * PackTrustCard.jsx — RC 1.48.4B3 (2026-05-04)
 *
 * Read-only attestation card that surfaces the same trust anchors
 * the exported premium PDF stamps into its /Info dictionary
 * (Producer / Author / Keywords). Lets a recruiter cross-check the
 * on-screen pack against the downloaded artefact in one glance.
 *
 *   • Pack run ID         (cv_variant_id — matches PDF Keywords)
 *   • Canonical run ID    (analyse_run_id from canonical fit)
 *   • Governance matrix   (active matrix version label)
 *   • Generated           (ISO timestamp of the preview render)
 *   • Pack version        (RC stamp)
 *
 * Pure read-only — sole input is the `trustAnchors` prop. No
 * fetches, no mutations, no LLM round-trips.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ShieldCheck } from 'lucide-react';

function _shortId(value) {
  if (!value || typeof value !== 'string') return '—';
  return value.length > 12 ? `${value.slice(0, 8)}…` : value;
}

function _formatTimestamp(iso) {
  if (!iso || typeof iso !== 'string') return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}

function Row({ label, value, full, testid }) {
  return (
    <div
      data-testid={testid}
      className="flex items-baseline justify-between gap-3 border-b border-ndor-edge last:border-0 py-1.5"
    >
      <span className="text-xs text-ndor-ink-muted">{label}</span>
      <span
        className="font-mono-ui text-[11px] text-ndor-ink truncate text-right"
        title={full || value}
      >
        {value}
      </span>
    </div>
  );
}

export default function PackTrustCard({ trustAnchors }) {
  const ta = trustAnchors || {};
  const packRunId      = ta.pack_run_id      || null;
  const canonicalRunId = ta.canonical_run_id || null;
  const matrixVersion  = ta.matrix_version   || null;
  const generatedAt    = ta.generated_at     || null;
  const packVersion    = ta.pack_version     || null;
  const verdict        = ta.governance_decision || null;

  return (
    <Card
      className="border-ndor-edge shadow-sm"
      data-testid="pack-trust-card"
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-ndor-ink flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-ndor-gold" aria-hidden />
          Trust attestation
        </CardTitle>
        <p
          data-testid="pack-trust-card-subline"
          className="text-[11px] text-ndor-ink-muted leading-relaxed"
        >
          These anchors are stamped into the exported PDF properties
          pane (Producer · Keywords) so a recruiter can verify
          provenance directly from Acrobat.
        </p>
      </CardHeader>
      <CardContent className="space-y-0">
        <Row
          label="Pack run ID"
          value={_shortId(packRunId)}
          full={packRunId || '—'}
          testid="pack-trust-row-pack-run-id"
        />
        <Row
          label="Canonical run ID"
          value={_shortId(canonicalRunId)}
          full={canonicalRunId || '—'}
          testid="pack-trust-row-canonical-run-id"
        />
        <Row
          label="Governance matrix"
          value={matrixVersion || '—'}
          full={matrixVersion || '—'}
          testid="pack-trust-row-matrix-version"
        />
        <Row
          label="Governance verdict"
          value={verdict ? verdict.toUpperCase() : '—'}
          testid="pack-trust-row-verdict"
        />
        <Row
          label="Pack version"
          value={packVersion || '—'}
          testid="pack-trust-row-pack-version"
        />
        <Row
          label="Generated"
          value={_formatTimestamp(generatedAt)}
          full={generatedAt || '—'}
          testid="pack-trust-row-generated-at"
        />
        <p
          data-testid="pack-trust-card-footer"
          className="text-[11px] text-ndor-ink-muted pt-2"
        >
          Read-only attestation — does not trigger a re-grade or charge.
        </p>
      </CardContent>
    </Card>
  );
}
